import type {
  ApiContext,
  ApiNotAuthorized,
  InvalidFormField,
  Location,
  LocationNotFound,
  LocationUpdate,
  NewLocation,
  PaymentsNotReady,
  ShipFrom,
  StoreNotFound,
  StoreRoleForbidden,
} from "@hobbyshopify/backend-client";
import type { AsyncResult } from "operation-result.js";
import type { AuthHandler } from "trunk.js";
import type { LocationsRepository } from "../repositories/locations-repository";
import type { BackendApi } from "./api";

interface LocationWrite {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  /** Marked stale after every write: the list carries the coordinates, the terminal id and the ship-from answer, so it is re-read rather than patched. */
  locationsRepository: LocationsRepository;
  storeId: string;
}

export async function createLocation(
  o: LocationWrite & { body: NewLocation },
): AsyncResult<Location, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | InvalidFormField> {
  const r = await o.authHandler.callApi((ctx) => o.client.locations.create(ctx, o.storeId, o.body));
  if (r.ok) o.locationsRepository.markStale();
  return r;
}

export async function updateLocation(
  o: LocationWrite & { locationId: string; body: LocationUpdate },
): AsyncResult<Location, ApiNotAuthorized | StoreNotFound | LocationNotFound | StoreRoleForbidden | InvalidFormField> {
  const r = await o.authHandler.callApi((ctx) => o.client.locations.update(ctx, o.storeId, o.locationId, o.body));
  if (r.ok) o.locationsRepository.markStale();
  return r;
}

/** Nominates the branch parcels leave from; null hands the choice back. */
export async function setShipFrom(
  o: LocationWrite & { locationId: string | null },
): AsyncResult<ShipFrom, ApiNotAuthorized | StoreNotFound | LocationNotFound | StoreRoleForbidden | InvalidFormField> {
  const r = await o.authHandler.callApi((ctx) => o.client.locations.setShipFrom(ctx, o.storeId, o.locationId));
  if (r.ok) o.locationsRepository.markStale();
  return r;
}

/** Registers the branch as a Stripe Terminal location, so a reader can be attached to it. */
export async function enableTerminal(
  o: LocationWrite & { locationId: string },
): AsyncResult<Location, ApiNotAuthorized | StoreNotFound | LocationNotFound | PaymentsNotReady> {
  const r = await o.authHandler.callApi((ctx) => o.client.locations.enableTerminal(ctx, o.storeId, o.locationId));
  if (r.ok) o.locationsRepository.markStale();
  return r;
}
